import React, { useEffect, useRef, useState } from "react";
import useNumericInput from "../../../hooks/useNumericInput";
import SunEditor from "suneditor-react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import CouponAPI from "../../../api/promo/couponApi";

function AddNew() {
  useNumericInput();

  const editorRef = useRef();
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState({});

  const [usageLimitCheck, setUsageLimitCheck] = useState(false);
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [percentage, setPercentage] = useState("");
  const [maximumDiscount, setMaximumDiscount] = useState("");
  const [minimumCartValue, setMinimumCartValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [usageLimitPerPerson, setUsageLimitPerPerson] = useState("");

  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (content) => {
    setMessage(content);
  };

  useEffect(() => {
    const parts = [];

    if (title) {
      parts.push(`This coupon code <strong>"${code}"</strong>`);
    }
    if (percentage) {
      parts.push(`provides a <strong>${percentage}%</strong> discount`);
    }
    if (maximumDiscount) {
      parts.push(`up to <strong>₹${maximumDiscount}</strong>.`);
    }
    if (startDate && endDate) {
      parts.push(
        `It is valid from <strong>${startDate}</strong> to <strong>${endDate}</strong>`
      );
    }
    if (minimumCartValue) {
      parts.push(
        `The coupon is applicable for carts above <strong>₹${minimumCartValue}</strong>.`
      );
    }
    if (usageLimit > 0) {
      parts.push(
        `First <strong>${usageLimit}</strong> customers would be able to use this coupon, `
      );
    }

    if (usageLimitPerPerson) {
      parts.push(
        `and each customer can use it up to <strong>${usageLimitPerPerson}</strong> times`
      );
    }

    parts.push(".");
    const finalDescription = parts.join(" ");
    setMessage(finalDescription);
  }, [
    title,
    code,
    percentage,
    maximumDiscount,
    minimumCartValue,
    startDate,
    endDate,
    usageLimit,
    usageLimitPerPerson,
  ]);

  const validate = () => {
    const newErrors = {};
    if (!title) {
      newErrors.title = "Title is required";
    }
    if (!code) {
      newErrors.code = "Code is required";
    }
    if (!percentage) {
      newErrors.percentage = "Percentage is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const editorContent = editorRef.current.getContents();

      const res = await CouponAPI.addDetails({
        title,
        code,
        percentage,
        maximumDiscount,
        minimumCartValue,
        startDate,
        endDate,
        usageLimit,
        usageLimitPerPerson,
        editorContent,
      });

      if (res.code === 200) {
        showSuccess(res.message);

        setTitle("");
        setCode("");
        setPercentage("");
        setMaximumDiscount("");
        setMinimumCartValue("");
        setStartDate("");
        setEndDate("");
        setUsageLimit("");
        setUsageLimitPerPerson("");
        setMessage("");
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }

    setIsLoading(false);
    hideLoader();
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add New Discount</h4>
            <h6>
              Create a new discount and apply it to specific products, brands,
              or categories.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Discount Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <h5 className="mb-1">Basic Information</h5>
              <div className="row mb-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Coupon Title (Not to be displayed){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Title (e.g., Diwali Offer)"
                    className="form-control"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>
              </div>

              <h5 className="mb-1">Discount Information</h5>
              <div className="row mb-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Coupon Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Code (e.g., DIWALI10)"
                    className="form-control"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  {errors.code && (
                    <span className="errors text-danger">{errors.code}</span>
                  )}
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Percentage (%) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Code (e.g., 10)"
                    maxLength={2}
                    className="form-control only-numeric"
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                  />
                  {errors.percentage && (
                    <span className="errors text-danger">
                      {errors.percentage}
                    </span>
                  )}
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Maximum Discount Amount</label>
                  <input
                    type="text"
                    placeholder="Amount (e.g., 100)"
                    className="form-control only-numeric"
                    value={maximumDiscount}
                    onChange={(e) => setMaximumDiscount(e.target.value)}
                  />
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Minimum Cart Value</label>
                  <input
                    type="text"
                    placeholder="Value (e.g., 500)"
                    className="form-control"
                    value={minimumCartValue}
                    onChange={(e) => setMinimumCartValue(e.target.value)}
                  />
                </div>
              </div>

              <h5 className="mb-1">Valid Information</h5>
              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Usage Limit</label>
                  <input
                    type="text"
                    placeholder="Value (e.g., 1000)"
                    className="form-control only-numeric"
                    value={usageLimit}
                    onChange={(e) => setUsageLimit(e.target.value)}
                    disabled={!usageLimitCheck}
                  />
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      value={usageLimitCheck}
                      onChange={(e) => setUsageLimitCheck(e.target.value)}
                    />
                    <label
                      className="form-check-label fs--1"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Enable Usage Limit
                    </label>
                  </div>
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Usage Limit Per User</label>
                  <input
                    type="text"
                    placeholder="Value (e.g., 1)"
                    className="form-control only-numeric"
                    value={usageLimitPerPerson}
                    onChange={(e) => setUsageLimitPerPerson(e.target.value)}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <SunEditor
                    getSunEditorInstance={(sunEditor) => {
                      editorRef.current = sunEditor;
                    }}
                    height="400px"
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["font", "fontSize"],
                        ["formatBlock"],
                        ["bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor"],
                        ["align", "list", "table"],
                        ["link", "image", "video"],
                        ["fullScreen", "preview", "print"],
                      ],
                      imageFileInput: true,
                    }}
                    setContents={message}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
